import { COLUMNS, COLUMN_GROUPS } from './excel-columns.utils';
import { calculateChunkSizes, getExportColumns } from './excel-pipeline.utils';

import ExcelJS from 'exceljs';
import { TableRowData } from '../types/excel.types';

const EXCEL_CONSTANTS = {
   SHEET_NAME: 'Filtered Data',
   HEADER_BG_COLOR: 'FFE0E0E0',
   BORDER_COLOR: 'FF000000',
   SEPARATOR_COLOR: 'FF666666',
   DEFAULT_WIDTH: 15,
   MIN_COLUMN_WIDTH: 10,
   MAX_COLUMN_WIDTH: 100,
   CHUNK_SIZE: 100,
   MAX_ROWS_PER_FILE: 100000,
} as const;

const toNumberIfNumeric = (value: any): any => {
   if (value === null || value === undefined || value === '') return value;
   if (typeof value === 'number') return value;
   const str = String(value).trim();
   if (str === '') return value;
   const asNumber = Number(str);
   if (!isNaN(asNumber) && isFinite(asNumber)) {
      return asNumber;
   }
   return value;
};

const addRightBorder = (cell: ExcelJS.Cell) => {
   cell.border = {
      ...cell.border,
      right: { style: 'thin', color: { argb: EXCEL_CONSTANTS.SEPARATOR_COLOR } },
   };
};

const setupWorksheet = (worksheet: ExcelJS.Worksheet, columnsToExport: string[]) => {
   worksheet.columns = columnsToExport.map((colId: string) => {
      const definition = COLUMNS[colId];
      return {
         header: definition?.header || colId,
         key: colId,
         width: EXCEL_CONSTANTS.DEFAULT_WIDTH,
      };
   });

   const headerRow = worksheet.getRow(1);
   headerRow.font = { bold: true };
   headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: EXCEL_CONSTANTS.HEADER_BG_COLOR },
   };
   headerRow.border = {
      bottom: { style: 'thick', color: { argb: EXCEL_CONSTANTS.BORDER_COLOR } },
   };

   worksheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 1 }];

   worksheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: columnsToExport.length },
   };
};

const calculateColumnWidths = (worksheet: ExcelJS.Worksheet) => {
   if (!worksheet.columns) return;

   for (const column of worksheet.columns) {
      if (!column) continue;

      let maxLength = 0;
      const rowCount = worksheet.rowCount;

      const cellsToCheck = new Set([
         1,
         ...Array.from({ length: Math.min(100, rowCount) }, (_, i) => i + 2),
         ...Array.from({ length: Math.min(100, rowCount) }, (_, i) => Math.max(2, rowCount - 100 + i)),
      ]);

      if (rowCount > 200) {
         for (let i = 100; i < rowCount - 100; i += 50) {
            cellsToCheck.add(i);
         }
      }

      column.eachCell?.({ includeEmpty: false }, (cell, rowNumber) => {
         if (!cellsToCheck.has(rowNumber)) return;

         const cellValue = cell.value;
         let cellLength = 0;

         if (cellValue !== null && cellValue !== undefined) {
            if (typeof cellValue === 'string') {
               cellLength = cellValue.length;
            } else if (typeof cellValue === 'number') {
               cellLength = cell.numFmt ? Math.max(cellValue.toString().length, 10) : cellValue.toString().length;
            } else if (cellValue instanceof Date) {
               cellLength = 10;
            } else {
               cellLength = String(cellValue).length;
            }
         }

         maxLength = Math.max(maxLength, cellLength);
      });

      const calculatedWidth = maxLength + 2;
      column.width = Math.max(EXCEL_CONSTANTS.MIN_COLUMN_WIDTH, Math.min(calculatedWidth, EXCEL_CONSTANTS.MAX_COLUMN_WIDTH));
   }
};

const processDataRow = (worksheet: ExcelJS.Worksheet, row: TableRowData, columnsToExport: string[], lastItemColumn: string | null) => {
   const rowData: Record<string, any> = {};

   for (const colId of columnsToExport) {
      const key = colId as keyof TableRowData;
      let value = row[key];

      if (value === undefined) continue;

      const columnDef = COLUMNS[colId];

      if (colId === 'isBase' && typeof value === 'boolean') {
         value = value ? 'Yes' : 'No';
      } else if (columnDef?.numeric && value !== null && value !== '') {
         value = toNumberIfNumeric(value);
      } else if (typeof value === 'string' && value.length > 32767) {
         value = value.substring(0, 32767);
      } else if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
         value = JSON.stringify(value).substring(0, 32767);
      }

      rowData[colId] = value;
   }

   const excelRow = worksheet.addRow(rowData);

   for (const colId of columnsToExport) {
      const columnDef = COLUMNS[colId];
      if (columnDef?.format && typeof rowData[colId] === 'number') {
         excelRow.getCell(colId).numFmt = columnDef.format;
      }
   }

   if (lastItemColumn) {
      addRightBorder(excelRow.getCell(lastItemColumn));
   }
};

export interface ExportProgress {
   processed: number;
   total: number;
   percentage: number;
}

export type ProgressCallback = (progress: ExportProgress) => void;
export type FileProgressCallback = (current: number, total: number) => void;

export const createWorkbookFromData = async (
   data: TableRowData[],
   onProgress?: ProgressCallback,
   signal?: AbortSignal
): Promise<ExcelJS.Workbook> => {
   if (!data || data.length === 0) {
      throw new Error('No data provided for export');
   }

   const workbook = new ExcelJS.Workbook();
   const worksheet = workbook.addWorksheet(EXCEL_CONSTANTS.SHEET_NAME);

   const visibleColumns = data[0]._visibleColumns;
   if (!visibleColumns || visibleColumns.length === 0) {
      throw new Error('No visible columns defined for export');
   }

   const columnsToExport = getExportColumns(visibleColumns);

   if (columnsToExport.length === 0) {
      throw new Error('No columns selected for export');
   }

   setupWorksheet(worksheet, columnsToExport);

   const allItemColumns = COLUMN_GROUPS.item;
   const lastItemColumnIndex = columnsToExport.reduce((lastIndex, colId, index) => {
      return allItemColumns.includes(colId) ? index : lastIndex;
   }, -1);
   const lastItemColumn = lastItemColumnIndex >= 0 ? columnsToExport[lastItemColumnIndex] : null;

   const totalRows = data.length;
   const progressInterval = Math.max(1, Math.floor(totalRows / 100));

   for (let i = 0; i < data.length; i++) {
      if (signal?.aborted) {
         throw new Error('Export cancelled');
      }

      processDataRow(worksheet, data[i], columnsToExport, lastItemColumn);

      if (onProgress && (i % progressInterval === 0 || i === data.length - 1)) {
         onProgress({
            processed: i + 1,
            total: totalRows,
            percentage: Math.round(((i + 1) / totalRows) * 100),
         });

         if (i % (progressInterval * 10) === 0) {
            await new Promise((resolve) => setTimeout(resolve, 0));
         }
      }
   }

   calculateColumnWidths(worksheet);

   if (onProgress) {
      onProgress({
         processed: totalRows,
         total: totalRows,
         percentage: 100,
      });
   }

   return workbook;
};

const exportSingleFile = async (
   data: TableRowData[],
   filename: string,
   onProgress?: ProgressCallback,
   signal?: AbortSignal
): Promise<void> => {
   if (signal?.aborted) {
      throw new Error('Export cancelled');
   }

   const workbook = await createWorkbookFromData(data, onProgress, signal);

   if (signal?.aborted) {
      throw new Error('Export cancelled');
   }

   const buffer = await workbook.xlsx.writeBuffer();

   if (signal?.aborted) {
      throw new Error('Export cancelled');
   }

   const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
   });
   const url = window.URL.createObjectURL(blob);
   const link = document.createElement('a');
   link.href = url;
   link.download = filename;
   document.body.appendChild(link);
   link.click();
   document.body.removeChild(link);
   window.URL.revokeObjectURL(url);
};

const exportToExcelMultipleFiles = async (
   data: TableRowData[],
   baseFilename: string,
   includeTimestamp: boolean,
   onProgress?: ProgressCallback,
   onFileProgress?: FileProgressCallback,
   signal?: AbortSignal
): Promise<void> => {
   const chunkSizes = calculateChunkSizes(data.length, EXCEL_CONSTANTS.MAX_ROWS_PER_FILE);
   const fileCount = chunkSizes.length;

   const exportedFiles: string[] = [];
   const failedFiles: Array<{ index: number; error: Error }> = [];

   let offset = 0;
   for (let fileIndex = 0; fileIndex < fileCount; fileIndex++) {
      if (signal?.aborted) {
         throw new Error('Export cancelled');
      }

      if (onFileProgress) {
         onFileProgress(fileIndex + 1, fileCount);
      }

      const chunkSize = chunkSizes[fileIndex];
      const chunk = data.slice(offset, offset + chunkSize);
      const currentOffset = offset;
      offset += chunkSize;

      const partFilename = baseFilename.replace('.xlsx', `_part${fileIndex + 1}of${fileCount}.xlsx`);
      const finalFilename = includeTimestamp
         ? partFilename.replace('.xlsx', `_${new Date().toISOString().slice(0, 10)}.xlsx`)
         : partFilename;

      try {
         await exportSingleFile(
            chunk,
            finalFilename,
            (progress) => {
               if (onProgress) {
                  const overallProgress = {
                     processed: currentOffset + progress.processed,
                     total: data.length,
                     percentage: Math.round(((currentOffset + progress.processed) / data.length) * 100),
                  };
                  onProgress(overallProgress);
               }
            },
            signal
         );

         exportedFiles.push(finalFilename);
         await new Promise((resolve) => setTimeout(resolve, 500));
      } catch (error) {
         const err = error instanceof Error ? error : new Error('Unknown error');

         if (err.message === 'Export cancelled') {
            throw err;
         }

         failedFiles.push({ index: fileIndex + 1, error: err });
         console.error(`Failed to export file ${fileIndex + 1}:`, err);
         continue;
      }
   }

   if (failedFiles.length > 0) {
      const successCount = exportedFiles.length;
      const failedIndexes = failedFiles.map((f) => f.index).join(', ');
      const firstError = failedFiles[0].error.message;

      throw new Error(
         `Export partially completed: ${successCount}/${fileCount} files exported successfully. ` +
         `Failed files: ${failedIndexes}. First error: ${firstError}. ` +
         `Please try exporting again or contact support.`
      );
   }
};

export const exportToExcel = async (
   data: TableRowData[],
   filename: string = 'filtered_data_export.xlsx',
   includeTimestamp: boolean = true,
   onProgress?: ProgressCallback,
   onFileProgress?: FileProgressCallback,
   signal?: AbortSignal
): Promise<void> => {
   if (data.length === 0) {
      throw new Error('No data to export');
   }

   const willSplit = data.length > EXCEL_CONSTANTS.MAX_ROWS_PER_FILE;
   const fileCount = willSplit ? Math.ceil(data.length / EXCEL_CONSTANTS.MAX_ROWS_PER_FILE) : 1;

   if (onFileProgress) {
      onFileProgress(0, fileCount);
   }

   if (willSplit) {
      return exportToExcelMultipleFiles(data, filename, includeTimestamp, onProgress, onFileProgress, signal);
   }

   const finalFilename = includeTimestamp ? filename.replace('.xlsx', `_${new Date().toISOString().slice(0, 10)}.xlsx`) : filename;

   if (onFileProgress) {
      onFileProgress(1, 1);
   }

   await exportSingleFile(data, finalFilename, onProgress, signal);
};
